import { Pencil, Trash2, User } from 'lucide-react'
import { buildRoleDisplayParts, formatRoleCompanyDisplay } from '../../utils/userManagementFormatters'
import type { UserManagementRolesSectionProps } from '../../types'

export function UserManagementRolesSection({
  user,
  canEditRole,
  canShowFinMgr,
  canRemoveRoleItem,
  canImpersonateRole,
  onRequestFinanceManager,
  onRequestRemoveRole,
  onRequestImpersonate,
  onRequestSalesRole,
  onEditRole,
}: UserManagementRolesSectionProps) {
  return (
    <>
      {user.roles && user.roles.length > 0 ? (
        <div className="overflow-x-auto -mx-4 sm:-mx-5">
          <table className="min-w-full divide-y divide-slate-100" data-testid="um-edit-roles-table">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-black uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-black uppercase tracking-wider">
                  Company
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-black uppercase tracking-wider">
                  Info
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-black uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {user.roles.map((role) => (
                <tr
                  key={role.role_id}
                  className="hover:bg-slate-50/50"
                  data-testid={`um-edit-roles-row-${role.role_id}`}
                >
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {(() => {
                      const parts = buildRoleDisplayParts(role)
                      return parts.boldPrefix ? (
                        <>
                          <b>{parts.boldPrefix}</b>
                          {parts.suffix}
                        </>
                      ) : (
                        parts.suffix
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {role.role_name === 'ROLE_LIBERTY_SALES' && role.sales_role !== 'SALES_LEAD' ? (
                      <div className="flex gap-3">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="radio"
                            name={`sales_role_${role.role_id}`}
                            value="SALES_EXEC"
                            checked={role.sales_role === 'SALES_EXEC'}
                            onChange={() => onRequestSalesRole(role.role_id, 'SALES_EXEC')}
                            className="text-[rgb(21_151_255)]"
                          />
                          Sales Exec
                        </label>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="radio"
                            name={`sales_role_${role.role_id}`}
                            value="ACC_MGR"
                            checked={role.sales_role === 'ACC_MGR'}
                            onChange={() => onRequestSalesRole(role.role_id, 'ACC_MGR')}
                            className="text-[rgb(21_151_255)]"
                          />
                          Acct Mgr
                        </label>
                      </div>
                    ) : (() => {
                      const companyLabel = formatRoleCompanyDisplay(role)
                      return companyLabel ? <b>{companyLabel}</b> : '—'
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-col gap-1">
                      {canShowFinMgr(role) && (
                        <label
                          className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 select-none group"
                          title={role.fin_mgr_attribute === 'YES' ? 'Remove Finance Manager' : 'Make Finance Manager'}
                        >
                          <input
                            type="checkbox"
                            checked={role.fin_mgr_attribute === 'YES'}
                            onChange={() => onRequestFinanceManager(role.role_id)}
                            className="w-4 h-4 accent-blue-600 rounded border-slate-300 cursor-pointer transition-colors"
                            data-testid={`um-edit-roles-finmgr-chk-${role.role_id}`}
                          />
                          <span className="group-hover:text-slate-900 transition-colors">Finance Manager</span>
                        </label>
                      )}
                      {role.error_message && (
                        <span className="text-xs text-rose-600">{role.error_message}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {canImpersonateRole(role) && (
                        <button
                          type="button"
                          title="Impersonate"
                          onClick={() => onRequestImpersonate(role.role_id, role.role_name)}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-[rgb(21_151_255)]/10 hover:text-[rgb(21_151_255)] transition-all"
                          aria-label="Impersonate"
                          data-testid={`um-edit-roles-impersonate-btn-${role.role_id}`}
                        >
                          <User className="h-4 w-4" />
                        </button>
                      )}
                      {canEditRole && role.role_name !== 'ROLE_RENTER' && (
                        <button
                          type="button"
                          title="Edit Role"
                          onClick={() => onEditRole(role)}
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                          aria-label="Edit role"
                          data-testid={`um-edit-roles-edit-btn-${role.role_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canRemoveRoleItem(role) && (
                        <button
                          type="button"
                          title="Remove Role"
                          onClick={() =>
                            onRequestRemoveRole(role.role_id, user.roles.length === 1)
                          }
                          className="rounded-lg p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-all"
                          aria-label="Remove role"
                          data-testid={`um-edit-roles-remove-btn-${role.role_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-4">No Roles</p>
      )}
    </>
  )
}
